import { Alert, Badge } from '@koyeb/design-system';
import { useFormContext } from 'react-hook-form';

import { useCatalogInstance, useServiceScaling } from 'src/api';
import { Link } from 'src/components/link';
import { useFeatureFlag } from 'src/hooks/feature-flag';
import { createTranslate } from 'src/intl/translate';

import { useScalingRules } from '../../helpers/scaling-rules';
import { ServiceForm } from '../../service-form.types';
import { useWatchServiceForm } from '../../use-service-form';

import { AutoscalingConfiguration } from './autoscaling';
import { ScaleToZeroConfiguration } from './scale-to-zero';
import { ScalingValues } from './scaling-values';

const T = createTranslate('modules.serviceForm.scaling');

export function ScalingConfiguration() {
  const serviceId = useWatchServiceForm('meta.serviceId');

  const { watch } = useFormContext<ServiceForm>();
  const instance = useCatalogInstance(watch('instance'));

  const isFreeInstance = instance?.id === 'free';
  const isEcoInstance = instance?.category === 'eco';
  const hasVolumes = watch('volumes').map((volume) => volume.name !== '').length > 0;
  const min = watch('scaling.min');
  const max = watch('scaling.max');
  const allowLightSleepOnNvidiaGpu = useFeatureFlag('allow-light-sleep-on-nvidia-gpu');
  const serviceScaling = useServiceScaling(serviceId ?? undefined);

  const { onScalingChanged } = useScalingRules();

  return (
    <>
      {isFreeInstance && <FreeInstanceInfo />}
      {serviceScaling && serviceId && <ManualScalingInfo serviceId={serviceId} />}

      <ScalingValues
        type={isEcoInstance && !isFreeInstance ? 'fixed' : 'autoscaling'}
        disabled={isFreeInstance || hasVolumes}
        onChanged={onScalingChanged}
      />

      <ScaleToZeroConfiguration
        isFreeInstance={isFreeInstance}
        isEcoInstance={isEcoInstance}
        hasVolumes={hasVolumes}
        allowLightSleepOnNvidiaGpu={allowLightSleepOnNvidiaGpu}
      />

      <AutoscalingConfiguration disabled={min === max || max === 1} hasVolumes={hasVolumes} />
    </>
  );
}

function FreeInstanceInfo() {
  return (
    <Badge color="blue" className="text-start">
      <T id="freeInstanceInfo" />
    </Badge>
  );
}

function ManualScalingInfo({ serviceId }: { serviceId: string }) {
  const link = (children: React.ReactNode) => (
    <Link to="/services/$serviceId" params={{ serviceId }} hash="scaling" className="font-medium text-blue">
      {children}
    </Link>
  );

  return <Alert variant="info" description={<T id="manualScalingEnabled" values={{ link }} />} />;
}
